/**
 * The foot of the calendar's List view: how much of the upcoming window is
 * showing, and the button for the next page.
 *
 * The caption is the list's only statement of what it covers — the List has
 * no navigator and no count line — so it names both the count and how far
 * ahead the window reaches. It is also a polite live region, so a screen
 * reader hears each page arrive; and when the last page is loaded and the
 * button goes, focus moves to it instead of falling to the page body.
 */
import { useEffect, useRef } from 'react';
import type { ReactElement } from 'react';

export interface WorkshopsCalendarListFooterProps {
  shown: number;
  total: number;
  /** The last day the window covers, e.g. "Jan 5". */
  through: string;
  onLoadMore: () => void;
}

export function WorkshopsCalendarListFooter({
  shown,
  total,
  through,
  onLoadMore,
}: WorkshopsCalendarListFooterProps): ReactElement {
  const captionRef = useRef<HTMLParagraphElement>(null);
  const focusCaptionNext = useRef(false);
  const hasMore = total > shown;

  useEffect(() => {
    if (!hasMore && focusCaptionNext.current) captionRef.current?.focus();
    focusCaptionNext.current = false;
  }, [hasMore]);

  return (
    <div style={{ marginTop: 16, textAlign: 'center' }}>
      <p
        ref={captionRef}
        tabIndex={-1}
        role="status"
        style={{ margin: 0, fontSize: 12, color: 'var(--nb-subtle)', outline: 'none' }}
      >
        Showing {Math.min(shown, total)} of {total} upcoming · through {through}
      </p>
      {hasMore && (
        <button
          type="button"
          className="nb-btn"
          style={{ marginTop: 10 }}
          onClick={() => {
            focusCaptionNext.current = true;
            onLoadMore();
          }}
        >
          Load more workshops
        </button>
      )}
    </div>
  );
}
